import React, { Component } from 'react'
import {
    Alert, Button, Drawer, Dropdown, Form, Input, Menu, message, Modal, Select, Space, Table, Tag, Tooltip, Typography
} from 'antd'
import {
    CopyOutlined, DeleteOutlined, DownOutlined, EditOutlined, FileTextOutlined, MoreOutlined,
    PlusOutlined, ReloadOutlined, SettingOutlined, StopOutlined, UnlockOutlined
} from '@ant-design/icons'
import {
    copyVibeDbAccount,
    createVibeDbAccount,
    deleteVibeDbAccount,
    disableVibeDbAccount,
    enableVibeDbAccount,
    getUserListForDropbox,
    getVibeAdminAppList,
    getVibeDeploymentPrompt,
    getVibeDbAccountDetail,
    getVibeDbAccountList,
    getVibeDbGrantLogList,
    getVibeDbPromptTemplate,
    resetVibeDbPromptTemplate,
    saveVibeDbPromptTemplate,
    updateVibeDbAccountPermissions,
} from '../../request'
import { buildVibeGitlabCodexPrompt } from '../Profile/vibeGitlabPrompt'
import VibeAppDetailDrawer from './VibeAppDetailDrawer'
import VibeCodexGuideDialog from './VibeCodexGuideDialog'

const { Option } = Select
const { Paragraph, Text } = Typography

const STATUS_MAP = {
    enabled: { text: '启用', color: 'success' },
    disabled: { text: '禁用', color: 'default' },
    deleted: { text: '删除', color: 'default' },
}

const PROJECT_ACCESS_META = {
    owner: { text: '自建', color: 'gold', priority: 0 },
    readwrite: { text: '读写', color: 'green', priority: 1 },
    readonly: { text: '只读', color: 'blue', priority: 2 },
}

export default class VibeDbAuthManagement extends Component {
    formRef = React.createRef()

    state = {
        dataSource: [],
        pagination: { current: 1, pageSize: 20, total: 0 },
        keyword: '',
        status: undefined,
        loading: false,
        users: [],
        projects: [],
        modalVisible: false,
        modalMode: 'create',
        modalSubmitting: false,
        selectedAccount: null,
        grantDraft: [],
        selectedProjectIds: [],
        selectedPermissionType: 'readonly',
        logVisible: false,
        logLoading: false,
        logs: [],
        promptVisible: false,
        promptAccountId: null,
        deploymentPromptVisible: false,
        deploymentPromptLoading: false,
        deploymentPromptText: '',
        deploymentPromptAccountName: '',
        deploymentPromptUnavailable: '',
        promptTemplateVisible: false,
        promptTemplateLoading: false,
        promptTemplateSaving: false,
        promptTemplateText: '',
        defaultPromptTemplateText: '',
        promptTemplateCustomized: false,
        projectDetailVisible: false,
        projectDetailId: null,
    }

    componentDidMount() {
        this.loadList()
        this.loadUsers()
        this.loadProjects()
    }

    loadList = (pageNum = this.state.pagination.current, pageSize = this.state.pagination.pageSize) => {
        const { keyword, status } = this.state
        this.setState({ loading: true })
        return getVibeDbAccountList({ pageNum, pageSize, keyword, status })
            .then(res => {
                const page = res || {}
                this.setState({
                    dataSource: page.list || [],
                    pagination: {
                        current: page.pageNum || pageNum,
                        pageSize: page.pageSize || pageSize,
                        total: page.total || 0,
                    },
                })
            })
            .catch(() => {})
            .finally(() => this.setState({ loading: false }))
    }

    loadUsers = () => {
        getUserListForDropbox({ type: 0 })
            .then(res => this.setState({ users: res || [] }))
            .catch(() => {})
    }

    loadProjects = () => {
        getVibeAdminAppList({ pageNum: 1, pageSize: 500 })
            .then(res => this.setState({ projects: res?.list || [] }))
            .catch(() => {})
    }

    openCreate = () => {
        this.setState({
            modalVisible: true,
            modalMode: 'create',
            selectedAccount: null,
            grantDraft: [],
            selectedProjectIds: [],
            selectedPermissionType: 'readonly',
        }, () => {
            this.formRef.current?.resetFields()
        })
    }

    openCopy = (record) => {
        this.setState({
            modalVisible: true,
            modalMode: 'copy',
            selectedAccount: record,
            grantDraft: [],
            selectedProjectIds: [],
            selectedPermissionType: 'readonly',
        }, () => {
            this.formRef.current?.resetFields()
        })
    }

    openEdit = (record) => {
        this.setState({
            modalVisible: true,
            modalMode: 'edit',
            selectedAccount: record,
            modalSubmitting: true,
            grantDraft: [],
            selectedProjectIds: [],
        })
        getVibeDbAccountDetail({ id: record.id })
            .then(res => {
                const grants = (res?.grants || []).map(item => ({
                    projectId: item.projectId,
                    projectName: item.projectName,
                    mysqlDatabaseName: item.mysqlDatabaseName,
                    ownerName: this.getProjectOwnerName(item),
                    description: this.getProjectDescription(item),
                    permissionType: item.permissionType || 'readonly',
                }))
                this.setState({ grantDraft: grants })
            })
            .catch(() => {})
            .finally(() => this.setState({ modalSubmitting: false }))
    }

    addGrant = () => {
        const { selectedProjectIds, selectedPermissionType, grantDraft, projects } = this.state
        if (!selectedProjectIds.length) {
            message.warning('请选择应用')
            return
        }
        const nextGrantDraft = this.mergeSelectedGrants(grantDraft, selectedProjectIds, selectedPermissionType, projects)
        if (nextGrantDraft.length === grantDraft.length) {
            message.warning('应用不存在')
            return
        }
        this.setState({
            grantDraft: nextGrantDraft,
            selectedProjectIds: [],
            selectedPermissionType: 'readonly',
        })
    }

    mergeSelectedGrants = (grantDraft, selectedProjectIds, selectedPermissionType, projects) => {
        const grantedProjectIds = grantDraft.map(item => String(item.projectId))
        const selectedProjects = projects.filter(item => selectedProjectIds.some(id => String(id) === String(item.id))
            && !grantedProjectIds.includes(String(item.id)))
        return grantDraft.concat(selectedProjects.map(project => ({
            projectId: project.id,
            projectName: project.projectName,
            mysqlDatabaseName: project.mysqlDatabaseName,
            ownerName: this.getProjectOwnerName(project),
            description: this.getProjectDescription(project),
            permissionType: selectedPermissionType || 'readonly',
        })))
    }

    updateGrantPermission = (projectId, permissionType) => {
        this.setState({
            grantDraft: this.state.grantDraft.map(item => String(item.projectId) === String(projectId)
                ? { ...item, permissionType }
                : item),
        })
    }

    removeGrant = (projectId) => {
        this.setState({
            grantDraft: this.state.grantDraft.filter(item => String(item.projectId) !== String(projectId)),
        })
    }

    submitModal = () => {
        const {
            modalMode, selectedAccount, grantDraft, selectedProjectIds, selectedPermissionType, projects,
        } = this.state
        this.formRef.current.validateFields().then(values => {
            this.setState({ modalSubmitting: true })
            // 2026-07-16 coder(lq): Include applications still selected in the picker so clicking OK directly does
            // not submit an empty grant list and silently revoke all permissions.
            const submittedGrantDraft = this.mergeSelectedGrants(
                grantDraft, selectedProjectIds, selectedPermissionType, projects
            )
            const grants = submittedGrantDraft.map(item => ({
                projectId: item.projectId,
                permissionType: item.permissionType,
            }))
            let request
            if (modalMode === 'edit') {
                request = updateVibeDbAccountPermissions({ accountId: selectedAccount.id, grants })
            } else if (modalMode === 'copy') {
                request = copyVibeDbAccount({ sourceAccountId: selectedAccount.id, targetUserId: values.targetUserId })
            } else {
                request = createVibeDbAccount({ userId: values.userId, grants })
            }
            request.then(res => {
                message.success(modalMode === 'edit' ? '授权已更新' : '账号已创建')
                this.setState({ modalVisible: false })
                this.loadList()
                if (res?.initialPassword) {
                    Modal.info({
                        title: '初始密码',
                        content: (
                            <Paragraph copyable={{ text: res.initialPassword }} style={{ marginBottom: 0 }}>
                                <Text code>{res.initialPassword}</Text>
                            </Paragraph>
                        ),
                    })
                }
            }).catch(() => {})
                .finally(() => this.setState({ modalSubmitting: false }))
        }).catch(() => {})
    }

    changeStatus = (record, nextStatus) => {
        const request = nextStatus === 'enabled'
            ? enableVibeDbAccount({ accountId: record.id })
            : disableVibeDbAccount({ accountId: record.id })
        return request.then(() => {
            message.success(nextStatus === 'enabled' ? '已启用' : '已禁用')
            this.loadList()
        }).catch(() => {})
    }

    confirmChangeStatus = (record, nextStatus) => {
        const enabling = nextStatus === 'enabled'
        Modal.confirm({
            title: `确认${enabling ? '启用' : '禁用'}账号？`,
            content: enabling
                ? `启用后会恢复账号 ${record.mysqlUsername} 的应用授权。`
                : `禁用后会撤销账号 ${record.mysqlUsername} 的应用授权，之后可以重新启用。`,
            okText: enabling ? '启用' : '禁用',
            onOk: () => this.changeStatus(record, nextStatus),
        })
    }

    deleteAccount = (record) => {
        Modal.confirm({
            title: '确认删除账号？',
            content: `删除后会撤销账号 ${record.mysqlUsername} 的应用授权。`,
            okText: '删除',
            okButtonProps: { danger: true },
            onOk: () => deleteVibeDbAccount({ accountId: record.id }).then(() => {
                message.success('已删除')
                this.loadList()
            }),
        })
    }

    openLogs = (record) => {
        this.setState({ logVisible: true, logLoading: true, logs: [] })
        getVibeDbGrantLogList({ accountId: record.id })
            .then(res => this.setState({ logs: res || [] }))
            .catch(() => {})
            .finally(() => this.setState({ logLoading: false }))
    }

    generatePrompt = (record) => {
        this.setState({
            promptVisible: true,
            promptAccountId: record?.id || null,
        })
    }

    generateDeploymentPrompt = (record) => {
        this.setState({
            deploymentPromptVisible: true,
            deploymentPromptLoading: true,
            deploymentPromptText: '',
            deploymentPromptAccountName: record?.userNick || record?.userName || record?.mysqlUsername || '',
            deploymentPromptUnavailable: '',
        })
        getVibeDeploymentPrompt({ accountId: record?.id })
            .then(res => {
                if (!res?.gitlabCredentialsReady) {
                    this.setState({ deploymentPromptUnavailable: '该用户尚未生成 VibeCoding 初始化凭据' })
                    return
                }
                this.setState({ deploymentPromptText: buildVibeGitlabCodexPrompt(res) })
            })
            .catch(() => {})
            .finally(() => this.setState({ deploymentPromptLoading: false }))
    }

    normalizePromptText = (text) => {
        if (!text) return ''
        // 2026-07-09 coder(lq): Older prompt responses stored escaped line breaks; render them as readable markdown.
        return text.replace(/\\r\\n/g, '\n').replace(/\\n/g, '\n').replace(/\\r/g, '\n')
    }

    copyDeploymentPrompt = () => {
        const { deploymentPromptText } = this.state
        if (!deploymentPromptText) return
        if (navigator.clipboard) {
            navigator.clipboard.writeText(deploymentPromptText).then(() => message.success('已复制'))
        } else {
            message.warning('当前浏览器不支持自动复制，请手动选择复制')
        }
    }

    openPromptTemplate = () => {
        this.setState({
            promptTemplateVisible: true,
            promptTemplateLoading: true,
            promptTemplateText: '',
            defaultPromptTemplateText: '',
            promptTemplateCustomized: false,
        })
        getVibeDbPromptTemplate()
            .then(res => this.setState({
                promptTemplateText: this.normalizePromptText(res?.prompt || ''),
                defaultPromptTemplateText: this.normalizePromptText(res?.defaultPrompt || ''),
                promptTemplateCustomized: !!res?.customized,
            }))
            .catch(() => {})
            .finally(() => this.setState({ promptTemplateLoading: false }))
    }

    savePromptTemplate = () => {
        const { promptTemplateText } = this.state
        if (!promptTemplateText || !promptTemplateText.trim()) {
            message.warning('Prompt不能为空')
            return
        }
        this.setState({ promptTemplateSaving: true })
        saveVibeDbPromptTemplate({ prompt: promptTemplateText })
            .then(() => {
                message.success('Prompt模板已保存')
                this.setState({ promptTemplateVisible: false })
            })
            .catch(() => {})
            .finally(() => this.setState({ promptTemplateSaving: false }))
    }

    resetPromptTemplate = () => {
        Modal.confirm({
            title: '恢复默认 Prompt？',
            content: '恢复后会删除后台维护的自定义模板，后续生成 Prompt 将使用代码内置默认版本。',
            okText: '恢复默认',
            onOk: () => resetVibeDbPromptTemplate().then(() => {
                message.success('已恢复默认Prompt')
                this.openPromptTemplate()
            }),
        })
    }

    getUserValue = (item) => item.userId || item.USER_ID || item.user_id || item.id || item.value

    getUserLabel = (item) => {
        const name = item.userNick || item.USER_NICK || item.user_nick || item.nick || item.label || ''
        const account = item.userName || item.USER_NAME || item.user_name || item.name || item.email || this.getUserValue(item)
        return name && account && name !== account ? `${name}（${account}）` : (name || account || '-')
    }

    getProjectOwnerName = (item = {}) => item.ownerUserNick || item.ownerUserName || item.ownerUserId || '-'

    getProjectDescription = (item = {}) => item.description || item.remark || item.projectDescription || '-'

    getProjectOptionLabel = (item = {}) => {
        const projectName = item.projectName || item.projectSlug || '-'
        return `${projectName} / ${this.getProjectOwnerName(item)} / ${this.getProjectDescription(item)}`
    }

    renderStatus = (status) => {
        const meta = STATUS_MAP[status] || { text: status || '-', color: 'default' }
        return <Tag color={meta.color}>{meta.text}</Tag>
    }

    renderProjectPermissions = (projects) => {
        if (!Array.isArray(projects) || !projects.length) {
            return <Text type="secondary">暂无</Text>
        }
        // 2026-07-20 coder(lq): Group owned, read-write and read-only apps while preserving their relative order.
        const items = projects.map((project, index) => {
            const owned = project.isOwner === true || project.isOwner === 1 || String(project.isOwner) === '1'
            const accessType = owned ? 'owner' : project.permissionType
            const meta = PROJECT_ACCESS_META[accessType] || { text: accessType || '-', color: 'default', priority: 3 }
            return {
                projectId: project.projectId,
                name: project.projectName || '-',
                description: project.description || '',
                meta,
                originalIndex: index,
            }
        }).sort((left, right) => left.meta.priority - right.meta.priority || left.originalIndex - right.originalIndex)
        return (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px 12px' }}>
                {items.map(item => (
                    <Tooltip key={`${item.name}-${item.originalIndex}`} title={item.description || '暂无备注'}>
                        <Tag
                            color={item.meta.color}
                            role="link"
                            tabIndex={0}
                            onClick={() => this.openProjectDetail(item.projectId)}
                            onKeyDown={event => {
                                if (event.key === 'Enter') this.openProjectDetail(item.projectId)
                            }}
                            style={{ margin: 0, maxWidth: '100%', display: 'inline-flex', alignItems: 'center', justifySelf: 'start', cursor: 'pointer' }}
                        >
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</span>
                            <span style={{ marginLeft: 6, flexShrink: 0 }}>{item.meta.text}</span>
                        </Tag>
                    </Tooltip>
                ))}
            </div>
        )
    }

    openProjectDetail = (projectId) => {
        if (!projectId) return
        // 2026-07-20 coder(lq): Keep the authorization list mounted while showing application details.
        this.setState({ projectDetailVisible: true, projectDetailId: projectId })
    }

    renderPromptMenu = (record) => (
        <Menu>
            <Menu.Item key="data-analysis" icon={<FileTextOutlined />} onClick={() => this.generatePrompt(record)}>
                Codex 查询数据指南
            </Menu.Item>
            <Menu.Item key="deployment" icon={<FileTextOutlined />} onClick={() => this.generateDeploymentPrompt(record)}>
                部署 Prompt
            </Menu.Item>
        </Menu>
    )

    renderMoreActionMenu = (record) => (
        <Menu>
            <Menu.Item key="copy" icon={<CopyOutlined />} onClick={() => this.openCopy(record)}>复制账号</Menu.Item>
            <Menu.Item
                key="status"
                icon={record.status === 'enabled' ? <StopOutlined /> : <UnlockOutlined />}
                onClick={() => this.confirmChangeStatus(record, record.status === 'enabled' ? 'disabled' : 'enabled')}
            >
                {record.status === 'enabled' ? '禁用账号' : '启用账号'}
            </Menu.Item>
            <Menu.Item key="logs" icon={<FileTextOutlined />} onClick={() => this.openLogs(record)}>操作日志</Menu.Item>
            <Menu.Divider />
            <Menu.Item key="delete" danger icon={<DeleteOutlined />} onClick={() => this.deleteAccount(record)}>
                删除账号
            </Menu.Item>
        </Menu>
    )

    renderColumns = () => [
        {
            title: '账号',
            key: 'account',
            width: 220,
            render: (_, record) => (
                <div>
                    <Text code>{record.mysqlUsername || '-'}</Text>
                    <div style={{ marginTop: 6 }}>{this.renderStatus(record.status)}</div>
                </div>
            ),
        },
        {
            title: '所属用户',
            key: 'user',
            width: 220,
            render: (_, record) => (
                <div>
                    <div>{record.userNick || record.userName || '-'}</div>
                    <Text type="secondary" style={{ fontSize: 12 }}>{record.userName || record.userId || '-'}</Text>
                </div>
            ),
        },
        {
            title: (
                <Space size={4} wrap>
                    <span style={{ marginRight: 4 }}>有权限应用</span>
                    <Tag color={PROJECT_ACCESS_META.owner.color} style={{ margin: 0 }}>自建</Tag>
                    <Tag color={PROJECT_ACCESS_META.readwrite.color} style={{ margin: 0 }}>读写</Tag>
                    <Tag color={PROJECT_ACCESS_META.readonly.color} style={{ margin: 0 }}>只读</Tag>
                </Space>
            ),
            key: 'projects',
            render: (_, record) => this.renderProjectPermissions(record.projects),
        },
        {
            title: '更新时间',
            dataIndex: 'updateTime',
            width: 170,
        },
        {
            title: '操作',
            key: 'action',
            fixed: 'right',
            width: 280,
            render: (_, record) => (
                <Space size={6}>
                    <Button size="small" icon={<EditOutlined />} onClick={() => this.openEdit(record)}>授权</Button>
                    <Dropdown overlay={this.renderPromptMenu(record)} trigger={['click']}>
                        <Button size="small" icon={<FileTextOutlined />}>Prompt <DownOutlined /></Button>
                    </Dropdown>
                    <Dropdown overlay={this.renderMoreActionMenu(record)} trigger={['click']}>
                        <Button size="small" icon={<MoreOutlined />}>更多 <DownOutlined /></Button>
                    </Dropdown>
                </Space>
            ),
        },
    ]

    renderGrantEditor = () => {
        const { projects, grantDraft, selectedProjectIds, selectedPermissionType } = this.state
        const grantedProjectIds = grantDraft.map(item => String(item.projectId))
        const availableProjects = projects.filter(item => !grantedProjectIds.includes(String(item.id)))
        return (
            <div>
                <Space style={{ marginBottom: 12 }} wrap>
                    <Select
                        mode="multiple"
                        showSearch
                        allowClear
                        value={selectedProjectIds}
                        placeholder="选择应用"
                        style={{ width: 520 }}
                        optionFilterProp="label"
                        optionLabelProp="title"
                        onChange={value => this.setState({ selectedProjectIds: value })}
                    >
                        {availableProjects.map(item => (
                            <Option
                                key={item.id}
                                value={item.id}
                                title={item.projectName || item.projectSlug || '-'}
                                label={this.getProjectOptionLabel(item)}
                            >
                                <div>
                                    <div>{item.projectName || item.projectSlug || '-'}</div>
                                    <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>所有者：{this.getProjectOwnerName(item)}</Text>
                                    <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>备注：{this.getProjectDescription(item)}</Text>
                                </div>
                            </Option>
                        ))}
                    </Select>
                    <Select
                        value={selectedPermissionType}
                        style={{ width: 120 }}
                        onChange={value => this.setState({ selectedPermissionType: value })}
                    >
                        <Option value="readonly">只读</Option>
                        <Option value="readwrite">读写</Option>
                    </Select>
                    <Button icon={<PlusOutlined />} onClick={this.addGrant} disabled={!selectedProjectIds.length}>加入授权</Button>
                </Space>
                <Table
                    size="small"
                    rowKey="projectId"
                    dataSource={grantDraft}
                    pagination={false}
                    columns={[
                        { title: '应用', dataIndex: 'projectName' },
                        { title: '所有者', dataIndex: 'ownerName', render: value => value || '-' },
                        { title: '备注', dataIndex: 'description', render: value => value || '-' },
                        {
                            title: '权限',
                            dataIndex: 'permissionType',
                            width: 140,
                            render: (value, record) => (
                                <Select value={value} style={{ width: 110 }} onChange={next => this.updateGrantPermission(record.projectId, next)}>
                                    <Option value="readonly">只读</Option>
                                    <Option value="readwrite">读写</Option>
                                </Select>
                            ),
                        },
                        {
                            title: '操作',
                            width: 90,
                            render: (_, record) => <Button size="small" danger onClick={() => this.removeGrant(record.projectId)}>移除</Button>,
                        },
                    ]}
                />
            </div>
        )
    }

    renderAccountModal = () => {
        const { modalVisible, modalMode, modalSubmitting, users, selectedAccount } = this.state
        const titleMap = { create: '创建数据库账号', edit: '修改账号授权', copy: '复制账号权限' }
        return (
            <Modal
                title={titleMap[modalMode]}
                visible={modalVisible}
                width={780}
                confirmLoading={modalSubmitting}
                onOk={this.submitModal}
                onCancel={() => this.setState({ modalVisible: false })}
                destroyOnClose
            >
                <Form ref={this.formRef} layout="vertical">
                    {modalMode === 'create' ? (
                        <Form.Item label="所属用户" name="userId" rules={[{ required: true, message: '请选择用户' }]}>
                            <Select showSearch placeholder="选择用户" optionFilterProp="children">
                                {users.map(item => <Option key={this.getUserValue(item)} value={this.getUserValue(item)}>{this.getUserLabel(item)}</Option>)}
                            </Select>
                        </Form.Item>
                    ) : null}
                    {modalMode === 'copy' ? (
                        <>
                            <Form.Item label="来源账号">
                                <Input value={selectedAccount?.mysqlUsername || ''} disabled />
                            </Form.Item>
                            <Form.Item label="目标用户" name="targetUserId" rules={[{ required: true, message: '请选择目标用户' }]}>
                                <Select showSearch placeholder="选择目标用户" optionFilterProp="children">
                                    {users.map(item => <Option key={this.getUserValue(item)} value={this.getUserValue(item)}>{this.getUserLabel(item)}</Option>)}
                                </Select>
                            </Form.Item>
                        </>
                    ) : null}
                    {modalMode !== 'copy' ? this.renderGrantEditor() : (
                        <Text type="secondary">将复制来源账号当前启用的应用授权，并为目标用户创建独立 MySQL 账号。</Text>
                    )}
                </Form>
            </Modal>
        )
    }

    renderLogDrawer = () => {
        const { logVisible, logLoading, logs } = this.state
        return (
            <Drawer title="授权操作日志" visible={logVisible} width={760} onClose={() => this.setState({ logVisible: false })}>
                <Table
                    rowKey="id"
                    size="small"
                    loading={logLoading}
                    dataSource={logs}
                    pagination={false}
                    columns={[
                        { title: '时间', dataIndex: 'createTime', width: 170 },
                        { title: '操作', dataIndex: 'operation', width: 150 },
                        { title: '状态', dataIndex: 'status', width: 100, render: value => <Tag color={value === 'success' ? 'success' : 'error'}>{value}</Tag> },
                        { title: '说明', dataIndex: 'message' },
                    ]}
                />
            </Drawer>
        )
    }

    renderDeploymentPromptModal = () => {
        const {
            deploymentPromptVisible,
            deploymentPromptLoading,
            deploymentPromptText,
            deploymentPromptAccountName,
            deploymentPromptUnavailable,
        } = this.state
        return (
            <Modal
                title={`部署 Prompt${deploymentPromptAccountName ? `：${deploymentPromptAccountName}` : ''}`}
                visible={deploymentPromptVisible}
                width={860}
                confirmLoading={deploymentPromptLoading}
                okText="复制"
                okButtonProps={{ disabled: !deploymentPromptText }}
                onOk={this.copyDeploymentPrompt}
                onCancel={() => this.setState({ deploymentPromptVisible: false })}
            >
                {deploymentPromptUnavailable ? (
                    <Alert
                        type="warning"
                        showIcon
                        style={{ marginBottom: 12 }}
                        message={deploymentPromptUnavailable}
                    />
                ) : null}
                <Input.TextArea
                    value={deploymentPromptText}
                    rows={22}
                    readOnly
                    style={{ fontFamily: 'Menlo, Monaco, Consolas, monospace', fontSize: 13, lineHeight: 1.6 }}
                />
            </Modal>
        )
    }

    renderPromptTemplateModal = () => {
        const {
            promptTemplateVisible,
            promptTemplateLoading,
            promptTemplateSaving,
            promptTemplateText,
            promptTemplateCustomized,
        } = this.state
        return (
            <Modal
                title="维护数据分析 Prompt"
                visible={promptTemplateVisible}
                width={960}
                confirmLoading={promptTemplateSaving}
                okText="保存"
                onOk={this.savePromptTemplate}
                onCancel={() => this.setState({ promptTemplateVisible: false })}
                destroyOnClose
                footer={[
                    <Button key="reset" onClick={this.resetPromptTemplate} disabled={promptTemplateLoading || !promptTemplateCustomized}>恢复默认</Button>,
                    <Button key="cancel" onClick={() => this.setState({ promptTemplateVisible: false })}>取消</Button>,
                    <Button key="save" type="primary" loading={promptTemplateSaving} onClick={this.savePromptTemplate}>保存</Button>,
                ]}
            >
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="这里维护的是授权管理所有账号共用的数据分析 Prompt 模板。每行的数据分析 Prompt 按账号授权范围同步数据字典，但模板正文共用这一份。"
                />
                <Input.TextArea
                    value={promptTemplateText}
                    rows={26}
                    disabled={promptTemplateLoading}
                    onChange={e => this.setState({ promptTemplateText: e.target.value })}
                    style={{ fontFamily: 'Menlo, Monaco, Consolas, monospace', fontSize: 13, lineHeight: 1.6 }}
                />
            </Modal>
        )
    }

    render() {
        const { dataSource, pagination, keyword, status, loading } = this.state
        return (
            <div style={{ padding: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <Space wrap>
                        <Input.Search
                            allowClear
                            placeholder="搜索账号、用户"
                            value={keyword}
                            onChange={e => this.setState({ keyword: e.target.value })}
                            onSearch={() => this.loadList(1)}
                            style={{ width: 240 }}
                        />
                        <Select allowClear placeholder="账号状态" value={status} style={{ width: 120 }} onChange={value => this.setState({ status: value }, () => this.loadList(1))}>
                            <Option value="enabled">启用</Option>
                            <Option value="disabled">禁用</Option>
                        </Select>
                        <Button icon={<ReloadOutlined />} onClick={() => this.loadList()}>刷新</Button>
                    </Space>
                    <Space>
                        <Button icon={<SettingOutlined />} onClick={this.openPromptTemplate}>维护数据分析 Prompt</Button>
                        <Button type="primary" icon={<PlusOutlined />} onClick={this.openCreate}>创建账号</Button>
                    </Space>
                </div>
                <Table
                    rowKey="id"
                    loading={loading}
                    dataSource={dataSource}
                    columns={this.renderColumns()}
                    scroll={{ x: 1100 }}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        showTotal: total => `共 ${total} 个账号`,
                    }}
                    onChange={page => this.loadList(page.current, page.pageSize)}
                />
                {this.renderAccountModal()}
                {this.renderLogDrawer()}
                <VibeCodexGuideDialog
                    visible={this.state.promptVisible}
                    accountId={this.state.promptAccountId}
                    onClose={() => this.setState({ promptVisible: false, promptAccountId: null })}
                />
                {this.renderDeploymentPromptModal()}
                {this.renderPromptTemplateModal()}
                <VibeAppDetailDrawer
                    visible={this.state.projectDetailVisible}
                    projectId={this.state.projectDetailId}
                    onClose={() => this.setState({ projectDetailVisible: false })}
                    onUpdated={() => this.loadList()}
                />
            </div>
        )
    }
}
